import { useMemo, useState } from "react";
import { Vacancy } from "../../models/vacancy/vacancy";
import WrapList from "../ui-lib/WrapList";
import GenericTeacherCard from "../teacher/GenericTeacherCard";
import UiDialog from "../ui-lib/UiDialog";
import { Teacher } from "../../models/teacher/teacher";
import TeacherDetails from "../teacher/TeacherDetails";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import AcceptTeacherApplicationDialog, {
  AcceptTeacherDialogResultProps,
} from "../../features/vacancy/AcceptTeacherApplicationDialog";
import { CheckIcon } from "@heroicons/react/20/solid";
import usePermissions from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../auth/permissions";

type VacancyTeacherOverviewProps = {
  vacancy: Vacancy;
  onAcceptTeacherApplication: (result: AcceptTeacherDialogResultProps) => void;
  onClick: (teacher: string) => void;
  activeTeacherCard?: string;
};

type TeacherStatus = "absent" | "accepted" | "applied" | "default";

function VacancyTeacherOverview({
  vacancy,
  onAcceptTeacherApplication,
  onClick,
  activeTeacherCard,
}: VacancyTeacherOverviewProps) {
  const { hasPermissions } = usePermissions();
  const [activeTeacher, setActiveTeacher] = useState<Teacher | undefined>();
  const [activeTeacherApplication, setActiveTeacherApplication] = useState<
    Teacher | undefined
  >();
  const teachers = useMemo(() => {
    return [
      vacancy.absentTeacher,
      ...(vacancy.teacherAccepted ?? []),
      ...(vacancy.teacherApplications ?? []),
    ];
  }, [vacancy]);

  const teacherStatus = (teacher: Teacher): TeacherStatus => {
    if (vacancy.absentTeacher.id === teacher.id) {
      return "absent";
    } else if (vacancy.teacherAccepted?.some((t) => t.id === teacher.id)) {
      return "accepted";
    } else if (vacancy.teacherApplications?.some((t) => t.id === teacher.id)) {
      return "applied";
    }
    return "default";
  };

  const avatarColor = (
    teacher: Teacher,
    mode: "avatar" | "text" = "avatar",
  ) => {
    const status = teacherStatus(teacher);
    const avatarMap: Record<TeacherStatus, string> = {
      absent: "border-red-500 bg-red-500",
      accepted: "border-green-900 bg-green-900",
      applied: "border-th-accent-900 bg-th-accent-900",
      default: "border-th-primary-900 bg-th-primary-900",
    };

    const textMap: Record<TeacherStatus, string> = {
      absent: "text-red-500",
      accepted: "text-green-900",
      applied: "text-th-accent-900",
      default: "text-th-primary-900",
    };
    return mode === "avatar" ? avatarMap[status] : textMap[status];
  };

  const teacherText = (teacher: Teacher) => {
    const status = teacherStatus(teacher);
    const textMap: Record<TeacherStatus, string> = {
      absent: "Ausfall",
      accepted: "Vertretung",
      applied: "Bewerbung",
      default: "Lehrperson",
    };
    return textMap[status];
  };

  const _onAcceptTeacherApplication = (
    result: AcceptTeacherDialogResultProps | undefined,
  ) => {
    setActiveTeacherApplication(undefined);

    if (result) {
      onAcceptTeacherApplication(result);
    }
  };

  const onClickButtonWrapper = (
    event: React.MouseEvent<HTMLButtonElement>,
    action: () => void,
  ) => {
    event.stopPropagation();
    action();
  };

  return (
    <>
      <WrapList items={teachers}>
        {(teacher) => (
          <GenericTeacherCard
            className={classNames({
              "border border-nid_grey px-2 py-1 shadow-none hover:cursor-pointer hover:opacity-100":
                true,
              "transition-opacity duration-200": true,
              "opacity-30":
                !!activeTeacherCard && teacher.id !== activeTeacherCard,
            })}
            key={teacher.id}
            teacher={teacher}
            avatarClass={avatarColor(teacher)}
            shadow={false}
            onClick={() => onClick(teacher.id)}
          >
            <GenericTeacherCard.Content>
              <div className="flex flex-col leading-none">
                <span
                  className={classNames(
                    "text-xs font-bold",
                    avatarColor(teacher, "text"),
                  )}
                >
                  {teacherText(teacher)}
                </span>
                <span className="font-bold">{teacher.fullName}</span>
              </div>
            </GenericTeacherCard.Content>
            <GenericTeacherCard.Action>
              <button
                onClick={(e) =>
                  onClickButtonWrapper(e, () => setActiveTeacher(teacher))
                }
              >
                <InformationCircleIcon className="h-5 w-5 text-th-primary-900" />
              </button>
            </GenericTeacherCard.Action>
            {teacherStatus(teacher) === "applied" &&
              hasPermissions([PERMISSIONS.CanAcceptTeacherOnVacancy]) && (
                <GenericTeacherCard.Action>
                  <button
                    onClick={(e) =>
                      onClickButtonWrapper(e, () =>
                        setActiveTeacherApplication(teacher),
                      )
                    }
                  >
                    <CheckIcon className="h-5 w-5 text-th-primary-900" />
                  </button>
                </GenericTeacherCard.Action>
              )}
          </GenericTeacherCard>
        )}
      </WrapList>
      <UiDialog
        open={!!activeTeacher}
        onClose={() => setActiveTeacher(undefined)}
        title="Lehrperson"
      >
        {activeTeacher && <TeacherDetails teacher={activeTeacher} />}
      </UiDialog>
      {activeTeacherApplication && (
        <AcceptTeacherApplicationDialog
          open={!!activeTeacherApplication}
          teacherApplication={activeTeacherApplication}
          vacancy={vacancy}
          onClose={_onAcceptTeacherApplication}
        />
      )}
    </>
  );
}

export default VacancyTeacherOverview;
